import { FC, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import { Box, formattedCurrency, capitalizeFirstLetterOfWords, withStyles, Card, sanitizeToNumeric } from 'vopay';
import { Title, DetailsRows, TransactionStatus } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type URLParams = {
  TransactionType: string;
  TransactionID: string;
  Currency: string;
};

const OtherTransactionInfo: FC = () => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.OtherTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { state } = useLocation<DataProps>();
  const { TransactionID } = useParams<URLParams>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();

  const transaction = useMemo(() => transactions[0], [transactions]);

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID,
      IncludeSubaccountTransactions: true,
    });
  }, [TransactionID, currentDate]);

  if (!transaction) return null;

  const transactionDetails = [
    {
      label: t('TransactionDetails.status', { defaultValue: 'Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={
              capitalizeFirstLetterOfWords(
                tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus || ''}`, {
                  defaultValue: transaction.TransactionStatus,
                }),
              ) || ''
            }
          />
        </Box>
      ),
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: transaction.TransactionType,
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${parseFloat(transaction.CreditAmount) === 0 ? formattedCurrency(transaction.DebitAmount) : formattedCurrency(transaction.CreditAmount) || '-'}`,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.paymentRailID', { defaultValue: 'Payment Rail ID' }),
      value: transaction.PaymentRailID || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${transaction.CreditAmount && Number(sanitizeToNumeric(transaction.CreditAmount)) !== 0 ? formattedCurrency(transaction.CreditAmount) : formattedCurrency(transaction.DebitAmount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.TransactionFailureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default OtherTransactionInfo;
